import React, { useEffect, useState } from 'react';
import { PrintSettings as PrintSettingsType } from '../types';
import { usePos } from '../context/PosContext';
import * as db from '../services/db';
import { printNetTest } from '../services/netPrinter';
import { buildTestEscPos } from '../services/escpos';
import { formatDate } from '../services/datetime';
import './Sections.css';
import './PrintSettings.css';

type Tab = 'printer' | 'settings' | 'header' | 'footer' | 'logo';
const TABS: { key: Tab; label: string }[] = [
  { key: 'printer', label: 'Printer Selection' },
  { key: 'settings', label: 'Print Settings' },
  { key: 'header', label: 'Header' },
  { key: 'footer', label: 'Footer' },
  { key: 'logo', label: 'LOGO' },
];

// Faithful port of print_setting_fragment_layout.xml: top tab buttons, a left
// card with the active section's controls, and a right card with the live
// receipt preview + a floating print button.
const PrintSettingsPage: React.FC = () => {
  const { config, servicePoints } = usePos();
  const [tab, setTab] = useState<Tab>('printer');
  const [logoTab, setLogoTab] = useState<'header' | 'footer'>('header');
  // Seed from the synchronous settings snapshot (primed at boot), then refresh
  // from the authoritative async store on mount.
  const [s, setS] = useState<PrintSettingsType>(() => db.getPrintSettingsSync());
  useEffect(() => {
    db.getPrintSettings().then(setS);
  }, []);

  const set = <K extends keyof PrintSettingsType>(k: K, v: PrintSettingsType[K]) =>
    setS((prev) => ({ ...prev, [k]: v }));

  // Set/clear a department (service-point) printer IP for Ethernet printing.
  const setDeptIp = (name: string, ip: string) =>
    setS((prev) => {
      const next = { ...(prev.departmentIps ?? {}) };
      if (ip.trim()) next[name] = ip;
      else delete next[name];
      return { ...prev, departmentIps: next };
    });

  // Keep the legacy paperWidth in sync with the chosen paper size on save.
  const paperWidthFor = (size: PrintSettingsType['paperSize']): PrintSettingsType['paperWidth'] =>
    size === '2 Inch' ? '58mm' : size === '3 Inch' ? '80mm' : 'A4';

  const save = () => {
    const next = { ...s, paperWidth: paperWidthFor(s.paperSize) };
    db.savePrintSettings(next);
    setS(next);
    alert('Print settings saved.');
  };

  const onLogoFile = (key: 'headerLogo' | 'footerLogo') => (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => set(key, reader.result as string);
    reader.readAsDataURL(file);
  };

  // ---- helpers shared with the preview ----
  const digits = parseInt(s.decimalDigits, 10);
  const money = (n: number) => n.toFixed(digits);
  const cur = config.currencySymbol;
  const title = s.title || config.storeName;
  const address = s.address || config.address || '';
  const gst = s.gstNo || config.gstin || '';
  const contact = s.contactNo || config.phone || '';

  const radios = <T extends string>(
    name: string,
    value: T,
    options: T[],
    onPick: (v: T) => void
  ) => (
    <div className="ps-radio-group" role="radiogroup" aria-label={name}>
      {options.map((opt) => (
        <button
          key={opt}
          type="button"
          className={`ps-radio ${value === opt ? 'active' : ''}`}
          onClick={() => onPick(opt)}
        >
          <span className="ps-radio-dot" />
          {opt}
        </button>
      ))}
    </div>
  );

  // Send a test page to an Ethernet printer IP the user just typed (uses the
  // current, possibly-unsaved, port). Only works inside the Android app.
  const testNet = async (ip: string) => {
    if (!ip.trim()) return alert('Enter a printer IP first.');
    try {
      await printNetTest(ip, s.ethernetPort, buildTestEscPos(title));
      alert(`Test page sent to ${ip.trim()}.`);
    } catch (e) {
      alert(`Could not reach ${ip.trim()}: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  // An IP input with an inline "Test" button (Ethernet printer config).
  const ipField = (label: string, value: string, onChange: (v: string) => void, placeholder: string) => (
    <div className="field">
      <label>{label}</label>
      <div className="ps-ip-row">
        <input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
        <button type="button" className="ps-ip-test" onClick={() => testNet(value)}>
          Test
        </button>
      </div>
    </div>
  );

  const headerFields: [keyof PrintSettingsType, string][] = [
    ['title', 'Title'],
    ['subTitle', 'Sub Title / Address'],
    ['fssai', 'FSSAI'],
    ['address', 'Address'],
    ['gstNo', 'GST No.'],
    ['contactNo', 'Contact No'],
    ['email', 'Email Id'],
    ['serialNo', 'Serial No'],
    ['taxInvoice', 'Tax Invoice'],
    ['parcelName', 'Parcel'],
    ['bankName', 'Bank Name'],
    ['bankBranch', 'Bank Branch'],
    ['bankIfsc', 'IFSC Code'],
    ['bankAccount', 'Account Number'],
  ];

  return (
    <div className="ps-page section-page">
      <div className="page-header ps-noprint">
        <h1>Print Settings</h1>
        <button className="btn-teal" onClick={save}>
          Save
        </button>
      </div>

      <div className="ps-tabs ps-noprint">
        {TABS.map((t) => (
          <button key={t.key} className={`ps-tab ${tab === t.key ? 'active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="ps-body">
        {/* ===== LEFT: active tab controls ===== */}
        <div className="ps-card ps-controls ps-noprint">
          {tab === 'printer' && (
            <>
              <div className="ps-section-label">Select printer type</div>
              {radios('printer type', s.printerType, ['Serial', 'Bluetooth', 'USB', 'Ethernet'], (v) =>
                set('printerType', v)
              )}

              {s.printerType === 'Ethernet' && (
                <>
                  <div className="ps-section-label">Ethernet printer IP addresses</div>
                  <div className="ps-fields">
                    {ipField('Billing Printer IP', s.billingIp, (v) => set('billingIp', v), 'e.g. 192.168.1.100')}
                    {ipField('KOT Printer IP', s.kotIp, (v) => set('kotIp', v), 'e.g. 192.168.1.101')}
                    <div className="field">
                      <label>Port</label>
                      <input
                        value={s.ethernetPort}
                        placeholder="9100"
                        onChange={(e) => set('ethernetPort', e.target.value)}
                      />
                    </div>
                  </div>
                  <p className="muted ps-note" style={{ marginTop: 8 }}>
                    Receipts print to the Billing IP. KOTs print to each department's IP below (falling
                    back to the KOT IP). Network printing runs only inside the Android app — the browser
                    falls back to the print dialog.
                  </p>

                  <div className="ps-section-label">Department printer IPs</div>
                  {servicePoints.length === 0 ? (
                    <p className="muted ps-note">
                      No departments (service points) configured yet. Add them on the Service Points
                      screen to assign a printer IP per department.
                    </p>
                  ) : (
                    <div className="ps-fields">
                      {servicePoints.map((sp) =>
                        <React.Fragment key={sp.id}>
                          {ipField(sp.name, s.departmentIps?.[sp.name] ?? '', (v) => setDeptIp(sp.name, v), 'e.g. 192.168.1.x')}
                        </React.Fragment>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="ps-section-label">Select printer paper size</div>
              {radios('paper size', s.paperSize, ['2 Inch', '3 Inch', 'A4 Size', 'Dot Matrix'], (v) =>
                set('paperSize', v)
              )}
              <label className="ps-check">
                <input type="checkbox" checked={s.boldFont} onChange={(e) => set('boldFont', e.target.checked)} />
                Enable bold font.
              </label>
              <p className="muted ps-note">
                The web app prints through the browser dialog to any installed printer. Native Serial/Bluetooth/USB
                pairing isn't available in the browser, but the chosen type & paper size are stored with the bill.
              </p>
            </>
          )}

          {tab === 'settings' && (
            <>
              <div className="ps-row2">
                <label className="ps-check">
                  <input
                    type="checkbox"
                    checked={s.netTotalFont21}
                    onChange={(e) => set('netTotalFont21', e.target.checked)}
                  />
                  NET TOTAL FONT SIZE 21 [2 Inch]
                </label>
                <label className="ps-check">
                  <input
                    type="checkbox"
                    checked={s.netTotalFont41}
                    onChange={(e) => set('netTotalFont41', e.target.checked)}
                  />
                  NET TOTAL FONT SIZE 41
                </label>
              </div>

              <div className="ps-section-label">Print digit after decimal Point #0.00</div>
              {radios('decimal', s.decimalDigits, ['0', '1', '2'], (v) => set('decimalDigits', v))}
              <div className="ps-radio-hint">#0 (Zero) · #0.0 (One) · #0.00 (Two)</div>

              <div className="ps-inline">
                <label>Select kitchen to split bill</label>
                <select value={s.splitKitchen} onChange={(e) => set('splitKitchen', e.target.value)}>
                  <option>None</option>
                  <option>Kitchen 1</option>
                  <option>Kitchen 2</option>
                  <option>Kitchen 3</option>
                </select>
              </div>

              <label className="ps-check">
                <input
                  type="checkbox"
                  checked={s.openCashDrawer}
                  onChange={(e) => set('openCashDrawer', e.target.checked)}
                />
                Open cash drawer with bill print.
              </label>

              <div className="ps-inline">
                <label>Copies</label>
                <input
                  type="number"
                  min={1}
                  max={5}
                  value={s.copies}
                  onChange={(e) => set('copies', parseInt(e.target.value) || 1)}
                />
              </div>
            </>
          )}

          {tab === 'header' && (
            <div className="ps-fields">
              {headerFields.map(([k, label]) => (
                <div className="field" key={k}>
                  <label>{label}</label>
                  <input
                    value={String(s[k] ?? '')}
                    placeholder={label}
                    onChange={(e) => set(k, e.target.value as never)}
                  />
                </div>
              ))}
            </div>
          )}

          {tab === 'footer' && (
            <div className="ps-fields">
              <label className="ps-check">
                <input
                  type="checkbox"
                  checked={s.doNotPrintFooters}
                  onChange={(e) => set('doNotPrintFooters', e.target.checked)}
                />
                Do not print footers
              </label>
              {([
                ['footer1', 'Footer Text 1'],
                ['footer2', 'Footer Text 2'],
                ['footer3', 'Footer Text 3'],
                ['footer4', 'Footer Text 4'],
                ['footer5', 'Footer Text 5'],
              ] as [keyof PrintSettingsType, string][]).map(([k, label]) => (
                <div className="field" key={k}>
                  <label>{label}</label>
                  <input
                    value={String(s[k] ?? '')}
                    placeholder={label}
                    disabled={s.doNotPrintFooters}
                    onChange={(e) => set(k, e.target.value as never)}
                  />
                </div>
              ))}
              <div className="ps-section-label">Configure Extra Charges</div>
              <div className="field">
                <label>Extra Charge</label>
                <input
                  value={s.extraCharge}
                  placeholder="EXTRA CHARGE"
                  onChange={(e) => set('extraCharge', e.target.value)}
                />
              </div>
            </div>
          )}

          {tab === 'logo' && (
            <>
              <div className="ps-logo-tabs">
                <button className={logoTab === 'header' ? 'active' : ''} onClick={() => setLogoTab('header')}>
                  Header Logo
                </button>
                <button className={logoTab === 'footer' ? 'active' : ''} onClick={() => setLogoTab('footer')}>
                  Footer Logo
                </button>
              </div>

              {logoTab === 'header' ? (
                <div className="ps-logo-pane">
                  <div className="ps-logo-box">
                    {s.headerLogo ? <img src={s.headerLogo} alt="header logo" /> : <span>No logo selected</span>}
                  </div>
                  <div className="ps-logo-actions">
                    <label className="btn-teal ps-upload">
                      Select Logo
                      <input type="file" accept="image/*" hidden onChange={onLogoFile('headerLogo')} />
                    </label>
                    {s.headerLogo && (
                      <button className="ps-link-danger" onClick={() => set('headerLogo', undefined)}>
                        Remove
                      </button>
                    )}
                  </div>
                  <label className="ps-check">
                    <input
                      type="checkbox"
                      checked={s.printHeaderLogo}
                      onChange={(e) => set('printHeaderLogo', e.target.checked)}
                    />
                    Do you want to print Logo (Header) on bill?
                  </label>
                </div>
              ) : (
                <div className="ps-logo-pane">
                  <div className="ps-logo-box">
                    {s.footerLogo ? <img src={s.footerLogo} alt="footer logo" /> : <span>No logo selected</span>}
                  </div>
                  <div className="ps-logo-actions">
                    <label className="btn-teal ps-upload">
                      Select Logo
                      <input type="file" accept="image/*" hidden onChange={onLogoFile('footerLogo')} />
                    </label>
                    {s.footerLogo && (
                      <button className="ps-link-danger" onClick={() => set('footerLogo', undefined)}>
                        Remove
                      </button>
                    )}
                  </div>
                  <label className="ps-check">
                    <input
                      type="checkbox"
                      checked={s.printFooterLogo}
                      onChange={(e) => set('printFooterLogo', e.target.checked)}
                    />
                    Do you want to print Logo (Footer) on bill?
                  </label>
                </div>
              )}
            </>
          )}
        </div>

        {/* ===== RIGHT: live receipt preview ===== */}
        <div className="ps-card ps-preview-card">
          <div className={`ps-receipt size-${s.paperSize === 'A4 Size' ? 'a4' : s.paperSize === '2 Inch' ? '2in' : '3in'}`}>
            {s.printHeaderLogo && s.headerLogo && <img className="ps-rc-logo" src={s.headerLogo} alt="logo" />}
            <div className={`ps-rc-title ${s.boldFont ? 'bold' : ''}`}>{title}</div>
            {s.subTitle && <div className="ps-rc-center">{s.subTitle}</div>}
            {address && <div className="ps-rc-center">{address}</div>}
            {s.fssai && <div className="ps-rc-center">FSSAI : {s.fssai}</div>}
            {gst && <div className="ps-rc-center">GSTIN : {gst}</div>}
            {contact && <div className="ps-rc-center">CONTACT NO. : {contact}</div>}
            {s.email && <div className="ps-rc-center">E-Mail : {s.email}</div>}
            <div className="ps-rc-center">{s.taxInvoice || 'TAX INVOICE'}</div>
            {s.parcelName && <div className="ps-rc-center">{s.parcelName}</div>}

            <div className="ps-rc-row">
              <span>{s.serialNo ? `SERIAL NO ${s.serialNo}` : 'SERIAL NO 101'}</span>
              <span>{formatDate(new Date())}</span>
            </div>
            <div className="ps-rc-row">
              <span>BILL NO 180213101</span>
              <span>12:45:00 PM</span>
            </div>

            <div className="ps-rc-hr" />
            <div className="ps-rc-cols">
              <span className="c-name">ITEM NAME</span>
              <span className="c-qty">QTY</span>
              <span className="c-rate">RATE</span>
              <span className="c-amt">AMT</span>
            </div>
            <div className="ps-rc-hr" />
            <div className="ps-rc-cols">
              <span className="c-name">ITEM 1</span>
              <span className="c-qty">{money(2)}</span>
              <span className="c-rate">{money(10)}</span>
              <span className="c-amt">{money(20)}</span>
            </div>
            <div className="ps-rc-hr" />

            <div className="ps-rc-row">
              <span>TOTAL QUANTITY</span>
              <span>{money(2)}</span>
            </div>
            <div className="ps-rc-row">
              <span>AMOUNT</span>
              <span>{cur} {money(20)}</span>
            </div>
            <div className={`ps-rc-row total ${s.netTotalFont41 ? 'big' : s.netTotalFont21 ? 'medium' : ''}`}>
              <span>TOTAL AMOUNT</span>
              <span>{cur} {money(20)}</span>
            </div>
            <div className="ps-rc-hr" />

            <div className="ps-rc-center">PAYMENT MODE : CASH</div>
            {s.extraCharge && (
              <>
                <div className="ps-rc-hr" />
                <div className="ps-rc-center">{s.extraCharge}</div>
              </>
            )}
            <div className="ps-rc-hr" />
            <div className="ps-rc-center">POWERED BY asnasoftware CONNECT</div>
            <div className="ps-rc-hr" />

            {!s.doNotPrintFooters &&
              [s.footer1, s.footer2, s.footer3, s.footer4, s.footer5]
                .filter(Boolean)
                .map((f, i) => (
                  <div className="ps-rc-center" key={i}>
                    {f}
                  </div>
                ))}
            {s.printFooterLogo && s.footerLogo && <img className="ps-rc-logo" src={s.footerLogo} alt="footer logo" />}
          </div>

          <button className="ps-print-fab ps-noprint" title="Print preview" onClick={() => window.print()}>
            🖨
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrintSettingsPage;
